/**
 * `TimeSeriesSection` — five inline-SVG charts driven by
 * `AdminMetricsOverview.series` (one bucket per period).
 *
 * Charts:
 *   - growth_new_accounts        → "New accounts" (new_accounts)
 *   - growth_active_accounts     → "Active accounts" (active_accounts)
 *     [also useful in Engagement; the parent picks which group renders it]
 *   - engagement_records         → "Discovery records" (discovery_records)
 *   - governance_mandates        → "Mandates" evaluated + approved overlay
 *   - revenue_transactions       → "Transactions succeeded"
 *   - revenue_volume             → "Volume — by currency"
 *
 * The parent passes a `charts` array selecting which series to render
 * (and optionally a `kicker` / `title` / `caption` override) so the
 * same section primitive can drive Growth, Engagement, Governance, and
 * Revenue category panels.
 *
 * Volume note: the per-bucket series doesn't carry the currency
 * breakdown (the API surfaces volume only in the headline `snapshot`).
 * The "revenue_volume" chart renders the `snapshot.volume` rows as a
 * caption-style panel rather than a line chart.
 */

import { Card } from '../../../components/atoms/Card.js';
import { LineAreaChart } from '../LineAreaChart.js';
import { SectionHeader } from '../KpiTile.js';
import { formatBucketDateUTC, formatCount, formatMinorCurrency } from '../../../lib/format.js';
import { useC } from '../../../lib/theme.js';

import type {
  AdminMetricsOverview,
  AdminMetricsSeriesPoint,
  AdminMetricsVolumeRow,
} from '@sill/types';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type TimeSeriesChart =
  | 'growth_new_accounts'
  | 'growth_active_accounts'
  | 'engagement_records'
  | 'governance_mandates'
  | 'revenue_transactions'
  | 'revenue_volume';

export type TimeSeriesState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; overview: AdminMetricsOverview };

export interface TimeSeriesSectionProps {
  state: TimeSeriesState;
  /** Which charts to render. Default = all six (the "All" category). */
  charts?: ReadonlyArray<TimeSeriesChart>;
  /** Section header overrides — caller passes the appropriate kicker
   *  + title + caption when slicing by category. */
  kicker?: string;
  title?: string;
  caption?: string;
}

const ALL_CHARTS: ReadonlyArray<TimeSeriesChart> = [
  'growth_new_accounts',
  'growth_active_accounts',
  'governance_mandates',
  'revenue_transactions',
  'revenue_volume',
];

export function TimeSeriesSection({
  state,
  charts = ALL_CHARTS,
  kicker = 'Time series',
  title = 'Daily activity',
  caption = 'One bucket per day in the selected window. Gaps are zero-filled by the server.',
}: TimeSeriesSectionProps): JSX.Element {
  if (state.kind === 'loading') {
    return (
      <section>
        <SectionHeader kicker={kicker} title={title} caption={caption} />
        <ChartGrid>
          {charts.map((c) => (
            <ChartLoadingCard key={`loading-${c}`} />
          ))}
        </ChartGrid>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section>
        <SectionHeader kicker={kicker} title={title} />
        <ChartErrorCard message={state.message} />
      </section>
    );
  }

  const series = state.overview.series;
  const xLabels = series.map((p) => formatBucketDateUTC(p.bucket_start));

  return (
    <section>
      <SectionHeader kicker={kicker} title={title} caption={caption} />
      <ChartGrid>
        {charts.map((chart) =>
          renderChart(chart, series, xLabels, state.overview.snapshot.volume),
        )}
      </ChartGrid>
    </section>
  );
}

function renderChart(
  chart: TimeSeriesChart,
  series: ReadonlyArray<AdminMetricsSeriesPoint>,
  xLabels: ReadonlyArray<string>,
  volume: ReadonlyArray<AdminMetricsVolumeRow>,
): JSX.Element {
  switch (chart) {
    case 'growth_new_accounts':
      return (
        <ChartCard
          key={chart}
          title="New accounts"
          subtitle="signups per day"
        >
          <LineAreaChart<AdminMetricsSeriesPoint>
            data={series}
            xLabels={xLabels}
            ariaLabel="New accounts per day"
            series={[
              {
                key: 'new_accounts',
                label: 'New accounts',
                color: '#D9FF3F',
                accessor: (p) => p.new_accounts,
              },
            ]}
            emptyState={<EmptyChart label="No new accounts in this window." />}
          />
        </ChartCard>
      );
    case 'growth_active_accounts':
      return (
        <ChartCard
          key={chart}
          title="Active accounts"
          subtitle="distinct accounts with traffic"
        >
          <LineAreaChart<AdminMetricsSeriesPoint>
            data={series}
            xLabels={xLabels}
            ariaLabel="Active accounts per day"
            series={[
              {
                key: 'active_accounts',
                label: 'Active accounts',
                color: '#7FB8FF',
                accessor: (p) => p.active_accounts,
              },
            ]}
            emptyState={<EmptyChart label="No active accounts in this window." />}
          />
        </ChartCard>
      );
    case 'engagement_records':
      return (
        <ChartCard
          key={chart}
          title="Discovery records"
          subtitle="audit_record rows per day (real activity)"
        >
          <LineAreaChart<AdminMetricsSeriesPoint>
            data={series}
            xLabels={xLabels}
            ariaLabel="Discovery records per day"
            series={[
              {
                key: 'discovery_records',
                label: 'Records',
                color: '#5AFFCD',
                accessor: (p) => p.discovery_records,
              },
            ]}
            emptyState={<EmptyChart label="No real activity in this window." />}
          />
        </ChartCard>
      );
    case 'governance_mandates':
      return (
        <ChartCard
          key={chart}
          title="Mandates"
          subtitle="evaluated vs approved"
        >
          <LineAreaChart<AdminMetricsSeriesPoint>
            data={series}
            xLabels={xLabels}
            ariaLabel="Mandates evaluated vs approved per day"
            series={[
              {
                key: 'mandates_evaluated',
                label: 'Evaluated',
                color: '#5AFFCD',
                accessor: (p) => p.mandates_evaluated,
              },
              {
                key: 'mandates_approved',
                label: 'Approved',
                color: '#D9FF3F',
                accessor: (p) => p.mandates_approved,
                fill: false,
              },
            ]}
            emptyState={<EmptyChart label="No mandates evaluated in this window." />}
          />
        </ChartCard>
      );
    case 'revenue_transactions':
      return (
        <ChartCard
          key={chart}
          title="Transactions"
          subtitle="succeeded per day"
        >
          <LineAreaChart<AdminMetricsSeriesPoint>
            data={series}
            xLabels={xLabels}
            ariaLabel="Transactions succeeded per day"
            series={[
              {
                key: 'transactions_succeeded',
                label: 'Succeeded',
                color: '#D9FF3F',
                accessor: (p) => p.transactions_succeeded,
                formatTooltip: (v) => formatCount(v),
              },
            ]}
            emptyState={<EmptyChart label="No transactions in this window." />}
          />
        </ChartCard>
      );
    case 'revenue_volume':
      return (
        <ChartCard
          key={chart}
          title="Volume — by currency"
          subtitle="per-currency totals over window · Live-mode settlements only"
        >
          <VolumeSnapshotPanel volume={volume} />
        </ChartCard>
      );
  }
}

/* ──────────────────────────── primitives ──────────────────────────── */

function ChartGrid({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{children}</div>;
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 18 }}>
      <div className="mb-3">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 2,
          }}
        >
          {title}
        </div>
        {subtitle !== undefined ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.dim,
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>
      {children}
    </Card>
  );
}

function ChartLoadingCard(): JSX.Element {
  const C = useC();
  return (
    <Card
      style={{
        padding: 18,
        minHeight: 280,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.dim,
          letterSpacing: 1.5,
        }}
      >
        Loading…
      </span>
    </Card>
  );
}

function ChartErrorCard({ message }: { message: string }): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 18 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.coralFg,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        Time series unavailable
      </div>
      <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>{message}</div>
    </Card>
  );
}

function EmptyChart({ label }: { label: string }): JSX.Element {
  const C = useC();
  return (
    <div
      className="flex items-center justify-center"
      style={{
        height: 220,
        background: C.surface2,
        border: `1px dashed ${C.borderSoft}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 11,
        color: C.muted,
        letterSpacing: 0.8,
        padding: 12,
        textAlign: 'center',
      }}
    >
      {label}
    </div>
  );
}

function VolumeSnapshotPanel({
  volume,
}: {
  volume: ReadonlyArray<AdminMetricsVolumeRow>;
}): JSX.Element {
  const C = useC();
  if (volume.length === 0) {
    return <EmptyChart label="No settlement volume in this window." />;
  }
  return (
    <div style={{ minHeight: 220, display: 'flex', flexDirection: 'column' }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {volume.map((row) => (
          <div
            key={row.currency}
            style={{
              background: C.surface2,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 2,
              padding: 12,
            }}
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 2,
                marginBottom: 4,
              }}
            >
              {row.currency.toUpperCase()}
            </div>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 20,
                color: C.text,
                fontWeight: 500,
                letterSpacing: -0.3,
                lineHeight: 1.2,
              }}
            >
              {formatMinorCurrency(row.gross_minor, row.currency)}
            </div>
            <div
              className="flex items-center justify-between"
              style={{
                marginTop: 6,
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
              }}
            >
              <span>net</span>
              <span style={{ color: C.text }}>
                {formatMinorCurrency(row.net_minor, row.currency)}
              </span>
            </div>
            <div
              className="flex items-center justify-between"
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
              }}
            >
              <span>refunded</span>
              <span style={{ color: C.coralFg }}>
                {formatMinorCurrency(row.refund_minor, row.currency)}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 'auto',
          paddingTop: 10,
          fontFamily: sansFont,
          fontSize: 10,
          color: C.dim,
          fontStyle: 'italic',
          lineHeight: 1.4,
        }}
      >
        Volume is never summed across currencies. Daily per-currency series is
        a TODO once the rollup surfaces it.
      </div>
    </div>
  );
}
